// src/components/shared/StockDimensionInputs.tsx
import { useState } from 'react'
import { STOCK_SHAPES, type ShapeId } from '../../lib/stock-shapes'
import StockDrawingUpload from './StockDrawingUpload'
import StockAlignmentViewer from './StockAlignmentViewer'
import { fetchStockAlignment, type PartPlacement } from '../../services/stockAlignment'

interface Props {
  shapeId: ShapeId
  values: Record<string, number>
  onChange(values: Record<string, number>): void
  units: 'mm' | 'in'
  disabled?: boolean
  /** The part's already-extracted geometry (from /api/analyze-files).
   *  Required for the irregular_drawing flow to align the part inside stock. */
  partGeometry?: Record<string, unknown>
  /** Called when the user confirms alignment in the irregular_drawing flow.
   *  Receives the placement and the stock geometry blob to persist. */
  onPlacementConfirm?(placement: PartPlacement, stockGeometry: Record<string, unknown>): void
}

const inputClass =
  'w-full px-2 py-1.5 bg-white/[0.03] border border-slate-800 rounded-sm ' +
  'font-mono text-sm text-slate-200 placeholder:text-slate-600 ' +
  'focus:border-cyan-500 focus:outline-none disabled:opacity-50'

export default function StockDimensionInputs({
  shapeId, values, onChange, units, disabled, partGeometry, onPlacementConfirm,
}: Props) {
  if (shapeId === 'irregular_drawing') {
    return (
      <IrregularDrawingFlow
        units={units}
        partGeometry={partGeometry}
        onChange={onChange}
        onPlacementConfirm={onPlacementConfirm}
      />
    )
  }

  const def = STOCK_SHAPES[shapeId]
  return (
    <div className="flex flex-wrap items-end gap-3">
      {def.dimensions.map((d) => (
        <div key={d.key} className="flex flex-col gap-1">
          <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
            {d.label} <span className="text-slate-700">({d.placeholder})</span>
          </label>
          <input
            type="number"
            min={d.min ?? 0}
            step={0.1}
            value={values[d.key] ?? ''}
            disabled={disabled}
            placeholder={d.placeholder}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value)
              onChange({ ...values, [d.key]: v })
            }}
            className={`${inputClass} w-28`}
          />
        </div>
      ))}
      <span className="font-mono text-xs text-slate-500 pb-2">{units}</span>
    </div>
  )
}

// ─── Irregular drawing flow ────────────────────────────────────────────────
//
// Isolated as a child component so its hooks live in a stable mount, instead
// of being conditional inside StockDimensionInputs (which would violate the
// rules of hooks).

interface IrregularDrawingFlowProps {
  units: 'mm' | 'in'
  partGeometry?: Record<string, unknown>
  onChange(values: Record<string, number>): void
  onPlacementConfirm?(placement: PartPlacement, stockGeometry: Record<string, unknown>): void
}

function IrregularDrawingFlow({
  units, partGeometry, onChange, onPlacementConfirm,
}: IrregularDrawingFlowProps) {
  const [stockGeometry, setStockGeometry] = useState<Record<string, unknown> | null>(null)
  const [alignment, setAlignment] = useState<{ placement: PartPlacement; reason?: string } | null>(null)
  const [aligning, setAligning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!stockGeometry) {
    return (
      <StockDrawingUpload
        units={units}
        onUploaded={async (geom) => {
          setStockGeometry(geom)
          setError(null)
          // If we don't have a part to align against, just capture the bbox
          // and exit. The user will see the alignment-skipped state.
          if (!partGeometry) {
            const env = (geom as { part_envelope?: { min_x: number; max_x: number; min_y: number; max_y: number; min_z: number; max_z: number } }).part_envelope
            if (env) {
              onChange({
                x: env.max_x - env.min_x,
                y: env.max_y - env.min_y,
                z: env.max_z - env.min_z,
              })
            }
            return
          }
          setAligning(true)
          try {
            const result = await fetchStockAlignment(geom, partGeometry)
            setAlignment({ placement: result.placement, reason: result.reason })
          } catch (e) {
            const m = e instanceof Error ? e.message : 'unknown'
            setError(`Alignment failed: ${m}`)
          } finally {
            setAligning(false)
          }
        }}
      />
    )
  }

  if (!partGeometry) {
    return (
      <div className="font-mono text-[10px] text-amber-400/80">
        Stock drawing captured, but no part geometry is available to align against yet.
        Upload the part drawing first, then choose this stock shape.
      </div>
    )
  }

  if (!alignment) {
    if (aligning) {
      return <div className="font-mono text-[10px] text-slate-400 animate-pulse">Aligning part inside stock…</div>
    }
    if (error) {
      return <div className="font-mono text-[10px] text-red-400">{error}</div>
    }
    return <div className="font-mono text-[10px] text-slate-400">Waiting for part geometry…</div>
  }

  return (
    <StockAlignmentViewer
      stockGeometry={stockGeometry}
      partGeometry={partGeometry}
      initialPlacement={alignment.placement}
      reason={alignment.reason}
      onConfirm={(placement) => {
        // Stock bbox is derived from the stock geometry's envelope.
        const env = (stockGeometry as { part_envelope?: { min_x: number; max_x: number; min_y: number; max_y: number; min_z: number; max_z: number } }).part_envelope
        const bbox = env
          ? { x: env.max_x - env.min_x, y: env.max_y - env.min_y, z: env.max_z - env.min_z }
          : { x: 0, y: 0, z: 0 }
        onChange({ x: bbox.x, y: bbox.y, z: bbox.z })
        onPlacementConfirm?.(placement, stockGeometry)
      }}
    />
  )
}
